/* DELLUMIA | DE LUXE terms within the EXISTING promotional popup, v1.2.
   Additive module; never changes BOGOS, discount calculations, or promo modal scripts.
   Loads only same-origin Shopify informational pages in the current promo dialog.
*/
(() => {
  'use strict';
  if (window.__dellumiaDeLuxeTermsInstalled) return;
  window.__dellumiaDeLuxeTermsInstalled = true;

  const MODAL = '.dellumia-bogos-modal';
  const DIALOG = '.dellumia-bogos-modal__dialog';
  const TERMS_LINK = '.dellumia-bogos-modal__terms[href]';
  const CONTENT_SELECTOR = 'section .container > .rte, section .page-width--narrow > .rte, .shopify-policy__body';
  const DISALLOWED = 'script, style, link, iframe, object, embed, form, input, textarea, button, select, template, noscript, svg, math, canvas, video, audio, source';
  const SAFE_TAGS = new Set('A P BR STRONG B EM I U S SMALL H2 H3 H4 H5 H6 UL OL LI DIV SPAN BLOCKQUOTE HR TABLE THEAD TBODY TFOOT TR TH TD SUP SUB IMG'.split(' '));
  const states = new WeakMap();
  const activeStates = new Set();
  let nextId = 0;

  function eligible(urlText, base = location.href) {
    try {
      const u = new URL(urlText, base);
      if (u.origin !== location.origin || !['http:', 'https:'].includes(u.protocol)) return null;
      if (!/^\/(?:pages|policies)\/[^/]+\/?$/.test(u.pathname)) return null;
      return u;
    } catch (_) { return null; }
  }
  function isTermsURL(url) {
    return url && url.pathname.replace(/\/$/, '') === '/pages/de-luxe-promotional-terms-conditions';
  }
  function findModal(element) {
    const modal = element.closest(MODAL);
    return modal && !modal.hidden && modal.isConnected ? modal : null;
  }
  function safeFocus(node) {
    if (!node?.isConnected) return;
    try { node.focus({ preventScroll: true }); } catch (_) { node.focus(); }
  }
  function isCurrent(state) {
    return states.get(state.modal) === state && state.modal.isConnected && !state.modal.hidden;
  }
  function eraseContent(state) {
    state.fetchController?.abort();
    state.fetchController = null;
    state.loadToken += 1;
  }
  function leaveTerms(state, focus = true) {
    if (!states.has(state.modal)) return;
    eraseContent(state);
    state.observer?.disconnect();
    state.removalObserver?.disconnect();
    state.view.remove();
    // Preserve the precise original order/structure. The renderers could still
    // update these nodes while terms were open because they stayed in the modal.
    for (const node of state.originalNodes) state.dialog.insertBefore(node, state.originalWrapper);
    state.originalWrapper.remove();
    if (!state.modal.hidden && state.modal.isConnected) refreshAnnouncementExplanation(state);
    state.dialog.classList.remove('dellumia-de-luxe-terms--active');
    state.dialog.style.removeProperty('--dellumia-de-luxe-original-height');
    if (state.oldLabel === null) state.dialog.removeAttribute('aria-labelledby');
    else state.dialog.setAttribute('aria-labelledby', state.oldLabel);
    if (state.oldDescribed === null) state.dialog.removeAttribute('aria-describedby');
    else state.dialog.setAttribute('aria-describedby', state.oldDescribed);
    state.dialog.scrollTop = state.oldScrollTop;
    states.delete(state.modal);
    activeStates.delete(state);
    if (focus && !state.modal.hidden) safeFocus(state.link);
  }
  function showError(state, url) {
    state.heading.textContent = 'Information';
    const p = document.createElement('p');
    p.textContent = 'Sorry, this information could not be loaded.';
    const a = document.createElement('a');
    a.href = url.href;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.textContent = 'Open the terms in a new tab';
    state.article.replaceChildren(p, a);
  }

  // Restrict the injected document to text-only and harmless semantic markup;
  // do not copy original classes, inline styles, scripts, IDs, or event handlers.
  function sanitize(source, baseURL, id) {
    const result = document.createDocumentFragment();
    const anchors = new Map();
    let serial = 0;
    function copyNode(node, parent) {
      if (node.nodeType === Node.TEXT_NODE) {
        parent.appendChild(document.createTextNode(node.nodeValue));
        return;
      }
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      const tag = node.tagName;
      if (node.matches(DISALLOWED)) return;
      if (!SAFE_TAGS.has(tag)) {
        for (const child of node.childNodes) copyNode(child, parent);
        return;
      }
      const out = document.createElement(tag.toLowerCase());
      const originalId = node.getAttribute('id') || (tag === 'A' ? node.getAttribute('name') : '');
      if (originalId) {
        const localId = `dellumia-de-luxe-terms-${id}-${++serial}`;
        if (!anchors.has(originalId)) anchors.set(originalId, localId);
        out.id = localId;
      }
      if (tag === 'IMG') {
        const src = node.getAttribute('src');
        try {
          const imageURL = new URL(src, baseURL.href);
          if (['http:', 'https:'].includes(imageURL.protocol)) out.src = imageURL.href;
          if (node.hasAttribute('alt')) out.alt = node.getAttribute('alt');
          out.loading = 'lazy';
        } catch (_) { return; }
      }
      if (tag === 'A' && node.hasAttribute('href')) {
        const href = node.getAttribute('href');
        const internal = eligible(href, baseURL.href);
        if (href?.startsWith('#')) {
          // Keep a real href for native Tab/keyboard semantics; intercept
          // activation in this module so the product URL never changes.
          out.href = href;
          out.setAttribute('data-dellumia-terms-hash', href.slice(1));
        } else if (internal && isTermsURL(internal)) {
          out.href = internal.href;
          out.setAttribute('data-dellumia-terms-page', '');
        } else if (internal) {
          // Other informational pages are NOT verified for extraction here.
          // Keep the shopping page open instead of displaying partial content.
          out.href = internal.href;
          out.target = '_blank';
          out.rel = 'noopener noreferrer';
        } else {
          try {
            const absolute = new URL(href, baseURL.href);
            if (['https:', 'http:', 'mailto:', 'tel:'].includes(absolute.protocol)) {
              out.href = absolute.href;
              out.target = '_blank';
              out.rel = 'noopener noreferrer';
            }
          } catch (_) { /* Unsafe links remain noninteractive. */ }
        }
      }
      if (['TH', 'TD'].includes(tag)) {
        for (const attribute of ['colspan', 'rowspan']) {
          const value = Number(node.getAttribute(attribute));
          if (Number.isInteger(value) && value >= 1 && value <= 20) out.setAttribute(attribute, String(value));
        }
      }
      for (const child of node.childNodes) copyNode(child, out);
      parent.appendChild(out);
    }
    for (const child of source.childNodes) copyNode(child, result);
    return { result, anchors };
  }

  function extract(html, url, id) {
    const page = new DOMParser().parseFromString(html, 'text/html');
    const main = page.querySelector('main#MainContent') || page.querySelector('main');
    if (!main) throw new Error('Missing informational main');
    // This feature deliberately supports only the verified DE LUXE terms page
    // using Dazzle's main-page template. Do not reuse this parser for arbitrary
    // pages or show only the first RTE when another informational section exists.
    if (!isTermsURL(url)) throw new Error('Unsupported informational URL');
    const contentNodes = [...main.querySelectorAll('section .container > .rte')];
    if (contentNodes.length !== 1) throw new Error('Unexpected DE LUXE terms template');
    const source = contentNodes[0];
    const heading = source.parentElement?.querySelector(':scope > h1');
    if (!heading || !source.textContent.trim()) throw new Error('Incomplete terms template');

    // Fail closed when any additional non-empty content is outside the verified
    // heading and RTE. This detects mixed-layout pages instead of silently
    // extracting their first familiar section. Shopify page metadata and inline
    // scripts/styles are excluded, but actual informational text is not.
    const remainder = main.cloneNode(true);
    remainder.querySelector('section .container > .rte')?.remove();
    remainder.querySelector('section .container > h1')?.remove();
    // Dazzle's verified main-page template includes a separate breadcrumb
    // navigation within <main>. Discard only that known navigational wrapper;
    // preserve fail-closed validation for any other informational content.
    for (const crumb of remainder.querySelectorAll('.breadcrumb_main')) {
      const nav = crumb.querySelector(':scope > nav.breadcrumb[aria-label="breadcrumbs"]');
      if (nav && [...crumb.children].every(child => child === nav)) crumb.remove();
    }
    remainder.querySelectorAll('script, style, template, noscript, svg').forEach(el => el.remove());
    if (remainder.textContent.replace(/\s+/g, '').length) {
      throw new Error('Additional informational content outside supported template');
    }
    const title = heading.textContent.trim() || 'DE LUXE Terms & Conditions';
    const clean = sanitize(source, url, id);
    return { title, ...clean };
  }
  function scrollToHash(state, name) {
    let decoded = name;
    try { decoded = decodeURIComponent(name); } catch (_) { /* Keep raw anchor. */ }
    const mapped = state.anchors?.get(decoded);
    if (!mapped) return;
    const anchor = state.article.querySelector(`[id="${CSS.escape(mapped)}"]`);
    if (!anchor) return;
    state.article.scrollTop += anchor.getBoundingClientRect().top - state.article.getBoundingClientRect().top - 8;
  }

  async function loadInfo(state, url) {
    eraseContent(state);
    const token = state.loadToken;
    const controller = new AbortController();
    state.fetchController = controller;
    state.anchors = new Map();
    state.currentURL = url;
    state.heading.textContent = 'DE LUXE Terms & Conditions';
    state.article.setAttribute('aria-busy', 'true');
    const status = document.createElement('p');
    status.setAttribute('role', 'status');
    status.textContent = 'Loading information…';
    state.article.replaceChildren(status);
    state.article.scrollTop = 0;
    try {
      const response = await fetch(url.href, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal: controller.signal
      });
      if (!response.ok || !response.headers.get('content-type')?.includes('text/html')) {
        throw new Error('Page could not be loaded as HTML');
      }
      const finalURL = eligible(response.url);
      if (!finalURL) throw new Error('Unexpected redirect');
      const data = extract(await response.text(), finalURL, token);
      if (token !== state.loadToken || !isCurrent(state)) return;
      state.heading.textContent = data.title;
      state.anchors = data.anchors;
      state.currentURL = finalURL;
      state.article.replaceChildren(data.result);
      if (url.hash) {
        requestAnimationFrame(() => {
          if (isCurrent(state) && token === state.loadToken) scrollToHash(state, url.hash.slice(1));
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError' && isCurrent(state) && token === state.loadToken) showError(state, url);
    } finally {
      if (token === state.loadToken && state.article.isConnected) state.article.removeAttribute('aria-busy');
    }
  }

  function findOwningBlock(modal) {
    // Both popup modules portal the open modal to <body>, so parentElement alone
    // cannot identify the source section. Use the original Liquid identifiers.
    if (modal.matches('[data-dlh-modal]')) {
      // The announcement renderer portals its modal to <body> while open.
      // Its Liquid title ID retains the original Shopify section ID:
      // DellumiaHomeModalTitle-{{ section.id }}. Resolve the OWNER from that
      // exact section rather than selecting another announcement bar.
      const titleId = modal.querySelector('[id^="DellumiaHomeModalTitle-"]')?.id;
      if (!titleId) return null;
      const sectionId = titleId.slice('DellumiaHomeModalTitle-'.length);
      const section = document.getElementById(`shopify-section-${sectionId}`);
      if (!section) return null;
      const matchingBars = section.querySelectorAll('[data-dellumia-home-progress]');
      return matchingBars.length === 1 ? matchingBars[0] : null;
    }
    const titleId = modal.querySelector('[id^="DellumiaBogosModalTitle-"]')?.id;
    if (!titleId) return null;
    const blockId = titleId.slice('DellumiaBogosModalTitle-'.length);
    return document.getElementById(`DellumiaBogosProgress-${blockId}`);
  }

  function refreshAnnouncementExplanation(state) {
    // The announcement modal is portaled outside its bar while open, so its
    // existing redrawModal(bar) cannot reach the modal at this moment. Mirror
    // only the already-computed display labels and merchant-set thresholds from
    // its live bar on Back. This never changes the cart or BOGOS discounts.
    if (!state.modal.matches('[data-dlh-modal]')) return;
    const bar = state.owner;
    if (!bar?.isConnected) return;
    for (let i = 1; i <= 4; i += 1) {
      const tier = bar.querySelector(`[data-dlh-goal="${i}"]`);
      const benefit = tier?.querySelector('.dellumia-home-progress__label strong')?.textContent?.trim() || '';
      const item = state.modal.querySelector(`[data-dlh-popup-goal="${i}"]`);
      const thresholdNode = item?.querySelector('[data-dlh-popup-threshold]');
      const benefitNode = item?.querySelector('[data-dlh-popup-benefit]');
      const amount = Number(bar.getAttribute(`data-goal-${i}`)) || 0;
      if (thresholdNode) thresholdNode.textContent = `Spend $${amount}${i >= 3 ? '+' : ''}`;
      if (benefitNode) benefitNode.textContent = i === 4
        ? `unlock a ${benefit}` : `Save ${benefit.replace(/\s*off$/i, '').trim()}`;
    }
  }

  function enterTerms(modal, link, url) {
    const dialog = modal.querySelector(DIALOG);
    if (!dialog || states.has(modal)) return;
    const closeButton = dialog.querySelector(':scope > .dellumia-bogos-modal__close');
    if (!closeButton) return;
    const measured = Math.ceil(dialog.getBoundingClientRect().height);
    const originalNodes = [...dialog.childNodes].filter(node => node !== closeButton);
    // Keep the original nodes INSIDE instance.modal. Both promotional renderers
    // query that modal for tier nodes. Moving the nodes to a DocumentFragment
    // prevents updates and results in stale content on Back.
    const originalWrapper = document.createElement('div');
    originalWrapper.className = 'dellumia-de-luxe-terms__original';
    originalWrapper.hidden = true;
    originalWrapper.inert = true;
    originalWrapper.setAttribute('aria-hidden', 'true');
    dialog.insertBefore(originalWrapper, originalNodes[0] || null);
    originalNodes.forEach(node => originalWrapper.appendChild(node));
    const id = `DellumiaDeLuxeTermsTitle-${++nextId}`;
    const view = document.createElement('div');
    view.className = 'dellumia-de-luxe-terms__view';
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'dellumia-de-luxe-terms__back';
    back.textContent = '← Back to DE LUXE';
    back.setAttribute('data-dellumia-de-luxe-terms-back', '');
    const heading = document.createElement('h2');
    heading.className = 'dellumia-de-luxe-terms__heading';
    heading.id = id;
    heading.textContent = 'DE LUXE Terms & Conditions';
    heading.tabIndex = 0;
    const article = document.createElement('div');
    article.className = 'dellumia-de-luxe-terms__article';
    article.setAttribute('role', 'region');
    article.setAttribute('aria-label', 'DE LUXE promotional terms');
    article.tabIndex = 0;
    view.append(back, heading, article);
    dialog.appendChild(view);
    const state = {
      modal, dialog, view, heading, article, back, link, originalNodes,
      originalWrapper, owner: findOwningBlock(modal), removalObserver: null,
      oldLabel: dialog.getAttribute('aria-labelledby'),
      oldDescribed: dialog.getAttribute('aria-describedby'),
      oldScrollTop: dialog.scrollTop, fetchController: null, loadToken: 0,
      anchors: new Map(), currentURL: url, observer: null
    };
    states.set(modal, state);
    activeStates.add(state);
    dialog.style.setProperty('--dellumia-de-luxe-original-height', `${Math.max(180, measured)}px`);
    dialog.classList.add('dellumia-de-luxe-terms--active');
    dialog.setAttribute('aria-labelledby', id);
    dialog.removeAttribute('aria-describedby');
    dialog.scrollTop = 0;
    state.observer = new MutationObserver(() => {
      if (modal.hidden || !modal.isConnected) leaveTerms(state, false);
    });
    state.observer.observe(modal, { attributes: true, attributeFilter: ['hidden'] });
    // Direct removal/section replacement may never change [hidden]. Observe
    // child removals only while Terms is active, then release strong references.
    state.removalObserver = new MutationObserver(records => {
      if (!records.some(record => record.removedNodes.length)) return;
      if (!modal.isConnected || (state.owner && !state.owner.isConnected)) leaveTerms(state, false);
    });
    state.removalObserver.observe(document.body, { childList: true, subtree: true });
    loadInfo(state, url);
    safeFocus(back);
  }

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!target || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;

    const termsLink = target.closest(TERMS_LINK);
    if (termsLink) {
      const modal = findModal(termsLink);
      const url = eligible(termsLink.getAttribute('href'));
      if (modal && isTermsURL(url)) {
        event.preventDefault();
        enterTerms(modal, termsLink, url);
      }
      return;
    }

    const back = target.closest('[data-dellumia-de-luxe-terms-back]');
    if (back) {
      const state = states.get(back.closest(MODAL));
      if (state) {
        event.preventDefault();
        leaveTerms(state, true);
      }
      return;
    }

    const link = target.closest('.dellumia-de-luxe-terms__article a[href], .dellumia-de-luxe-terms__article a[data-dellumia-terms-hash]');
    if (!link) return;
    const state = states.get(link.closest(MODAL));
    if (!state) return;
    const hash = link.getAttribute('data-dellumia-terms-hash');
    if (hash !== null) {
      event.preventDefault();
      scrollToHash(state, hash);
      return;
    }
    const url = eligible(link.getAttribute('href'), state.currentURL.href);
    if (url && isTermsURL(url)) {
      event.preventDefault();
      if (url.pathname === state.currentURL.pathname && url.search === state.currentURL.search && url.hash) {
        scrollToHash(state, url.hash.slice(1));
      } else {
        loadInfo(state, url);
      }
    }
  }, true);

  // Covers direct programmatic modal removal and Shopify dynamic section swaps.
  document.addEventListener('shopify:section:unload', event => {
    for (const state of [...activeStates]) {
      // Shopify fires unload before DOM detachment. A portaled modal remains
      // under body, so check the owning block too, not only modal connectivity.
      if (state.modal.hidden || !state.modal.isConnected ||
          (state.owner && !state.owner.isConnected) ||
          event.target?.contains(state.owner) || event.target?.contains(state.modal)) {
        leaveTerms(state, false);
      }
    }
  });
})();
